import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Archive, MessageSquare, Star, LayoutGrid, List, ArrowUpDown } from 'lucide-react';
import { reviewsApi } from '../../api/endpoints/reviews';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { FilterDropdown } from '../../components/ui/FilterDropdown';
import { Input } from '../../components/ui/Input';
import { DateFilterBar } from '../../components/ui/DateFilterBar';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import { StarRating } from '../../components/ui/StarRating';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Alert';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/ui/Pagination';
import { formatRelativeTime } from '../../lib/formatters';
import type { Review } from '../../types/review';

export function ReviewList() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => (localStorage.getItem('reviews_view') as 'grid' | 'list') || 'grid');
  const setView = (mode: 'grid' | 'list') => {
    setViewMode(mode);
    localStorage.setItem('reviews_view', mode);
  };

  const [typeFilter, setTypeFilter] = useState<string>('');
  const [ratingFilter, setRatingFilter] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { page, limit, goToPage } = usePagination({
    resetDeps: [typeFilter, ratingFilter, sortDir, startDate, endDate],
  });
  const [respondTarget, setRespondTarget] = useState<Review | null>(null);
  const [responseText, setResponseText] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Review | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['reviews', storeId, { page, limit, type: typeFilter, rating: ratingFilter, sort: sortDir, startDate, endDate }],
    queryFn: () => reviewsApi.list(storeId!, {
      page, limit,
      type: typeFilter || undefined,
      rating: ratingFilter ?? undefined,
      sort: sortDir !== 'desc' ? sortDir : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    enabled: !!storeId,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      reviewsApi.update(storeId!, id, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews', storeId] });
      toast('success', t('common.success'));
      setRespondTarget(null);
      setResponseText('');
    },
    onError: () => toast('error', t('common.error')),
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      reviewsApi.archive(storeId!, id, archived),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const sentimentBadge = (rating: number) => {
    if (rating === 5) return <Badge variant="success">{t('reviews.excellent', 'Excellent')}</Badge>;
    if (rating === 4) return <Badge variant="info">{t('reviews.good', 'Good')}</Badge>;
    if (rating === 3) return <Badge variant="warning">{t('reviews.acceptable', 'Acceptable')}</Badge>;
    return <Badge variant="danger">{t('reviews.weak', 'Weak')}</Badge>;
  };

  const reviews = data?.reviews ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.pages ?? 1;

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <div>
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold">{t('sidebar.reviews')}</h1>
            {total != null && total > 0 && (
              <span className="rounded-full bg-bg-tertiary px-2.5 py-0.5 text-xs font-medium text-text-secondary">
                {total}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-text-secondary">{t('reviews.description', 'Customer reviews and ratings')}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            {['', 'product', 'store'].map((type) => (
              <Button
                key={type}
                variant={typeFilter === type ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setTypeFilter(type)}
              >
                {type ? t(`reviews.${type}`) : t('common.all')}
              </Button>
            ))}
          </div>
          {/* View toggle */}
          <div className="flex items-center rounded-lg border border-border-default">
            <button
              onClick={() => setView('grid')}
              className={`flex items-center justify-center rounded-s-lg p-2 transition-colors ${
                viewMode === 'grid'
                  ? 'bg-accent text-white'
                  : 'text-text-muted hover:bg-bg-hover hover:text-text-primary'
              }`}
              title={t('common.gridView', 'Grid view')}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setView('list')}
              className={`flex items-center justify-center rounded-e-lg p-2 transition-colors ${
                viewMode === 'list'
                  ? 'bg-accent text-white'
                  : 'text-text-muted hover:bg-bg-hover hover:text-text-primary'
              }`}
              title={t('common.listView', 'List view')}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Star rating filter */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setRatingFilter(null)}
          className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
            ratingFilter === null
              ? 'bg-accent text-white'
              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
          }`}
        >
          {t('common.all')}
        </button>
        {/* 0 is a real value: the old product review widget defaulted its star
            picker to 0, so stores carry 0-star rows that were unreachable
            while this row started at 1. */}
        {[5, 4, 3, 2, 1, 0].map((stars) => (
          <button
            key={stars}
            onClick={() => setRatingFilter(stars)}
            className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              ratingFilter === stars
                ? 'bg-warning/20 text-warning border border-warning/30'
                : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
            }`}
          >
            <Star className={`h-3.5 w-3.5 ${ratingFilter === stars ? 'fill-warning text-warning' : 'text-text-muted'}`} />
            {stars}
          </button>
        ))}
      </div>

      {/* Sort & date filters */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <FilterDropdown
          icon={ArrowUpDown}
          options={[
            { value: 'desc', label: t('common.newestFirst', 'الأحدث أولاً') },
            { value: 'asc', label: t('common.oldestFirst', 'الأقدم أولاً') },
          ]}
          value={sortDir}
          onChange={(v) => setSortDir(v as 'desc' | 'asc')}
        />
        <DateFilterBar
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />
      </div>

      {reviews.length === 0 ? (
        <EmptyState title={t('common.noResults')} />
      ) : (
        <>
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
            {reviews.map((review) => (
              <div
                key={review._id}
                className="rounded-xl border border-border-default bg-bg-secondary p-5"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <StarRating rating={review.rating} />
                    {sentimentBadge(review.rating)}
                    {review.type && <Badge>{t(`reviews.${review.type}`, review.type)}</Badge>}
                  </div>
                  <PermissionGate requires="reviews.edit">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { setRespondTarget(review); setResponseText(review.response ?? ''); }}
                        className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
                        title={t('reviews.respond')}
                      >
                        <MessageSquare className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(review)}
                        className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-danger"
                        title={t('reviews.archive')}
                      >
                        <Archive className="h-4 w-4" />
                      </button>
                    </div>
                  </PermissionGate>
                </div>
                <p className="text-sm text-text-primary mb-2">{review.comment || '-'}</p>
                {review.response && (
                  <div className="mt-2 border-s-2 border-accent ps-3">
                    <p className="text-xs text-text-muted mb-1">{t('reviews.yourResponse')}</p>
                    <p className="text-sm text-text-secondary">{review.response}</p>
                  </div>
                )}
                <div className="mt-3 flex items-center gap-3 text-xs text-text-muted">
                  <span>{review.customerName || review.customerEmail || '-'}</span>
                  {review.productTitle && <span>• {review.productTitle}</span>}
                  {review.createdAt && <span>• {formatRelativeTime(review.createdAt)}</span>}
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination page={page} totalPages={totalPages} total={total} limit={limit} onPageChange={goToPage} />
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={!!respondTarget}
        onClose={() => setRespondTarget(null)}
        title={t('reviews.respond')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRespondTarget(null)}>{t('common.cancel')}</Button>
            <Button
              isLoading={updateMutation.isPending}
              onClick={() => respondTarget && updateMutation.mutate({ id: respondTarget._id, data: { response: responseText } })}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <Textarea
          label={t('reviews.response')}
          rows={4}
          value={responseText}
          onChange={(e) => setResponseText(e.target.value)}
        />
      </Modal>

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('reviews.archiveConfirmTitle', 'Archive Review')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
            <Button
              isLoading={archiveMutation.isPending}
              onClick={() => {
                if (deleteTarget) {
                  archiveMutation.mutate(
                    { id: deleteTarget._id, archived: !deleteTarget.archived },
                    { onSuccess: () => setDeleteTarget(null) },
                  );
                }
              }}
            >
              {t('reviews.archive', 'Archive')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">
          {t('reviews.archiveConfirm', 'Are you sure you want to archive this review?')}
        </p>
      </Modal>
    </div>
  );
}
